import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Navbar from '../components/Navbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { createDeliveryClusters, fetchDrivers, deleteDriver, fetchClustersByIterationId, updateShipmentDriver, updateDriver, addDriver, requestOptimalRoutes } from '../utils/api';
import { toast } from 'react-toastify';
import { useWebSocket } from '../contexts/WebSocketContext';
import MarkShipmentsMap from '../components/MarkShipmentsMap';
import EditDriverModal from '../components/EditDriverModal';

// Define the function outside of component or move it inside the component 
// but outside of other hooks or effects
async function processWebSocketMessage(data, pendingRequestRef, deliveryDate, numberOfDrivers, setDriversPlans, setIsLoading, setIsRouteOptimizing, navigate, selectedDrivers, setCurrentIterationId, currentIterationId) {
  
  // Check if this is a response to our makebestclusters request
  if ((data.script === 'makebestclusters' || data.script === 'transferShipment') && pendingRequestRef.current) {
    if (data.status === 'success') {
      console.log('Clustering completed successfully:', data);
      toast.success('Clustering completed successfully!');

      // Extract iteration_id from the webhook payload
      const iterationId = data.iteration_id;
      setCurrentIterationId(iterationId);
      
      try {
        // Fetch the clusters data using the iteration_id
        const clustersData = await fetchClustersByIterationId(iterationId);
        console.log('Fetched clusters data:', clustersData);
        
        // Transform the data to match the format expected by the map component
        const transformedData = clustersData.map(shipment => {
          // Find the corresponding driver from selectedDrivers
          const driverId = shipment.driver_id;
          const driverNumber = selectedDrivers.indexOf(driverId) + 1; // Convert to 1-based index
          const isPickup = shipment.delivery_partner === 'np_pickup' || shipment.delivery_partner === 'Unknown';
          
          return {
            'Delivery Name': shipment.number,
            'Latitude': isPickup ? shipment.sender?.address?.latitude : shipment.recipient?.address?.latitude,
            'Longitude': isPickup ? shipment.sender?.address?.longitude : shipment.recipient?.address?.longitude,
            'Address': isPickup ? shipment.sender?.address?.string : shipment.recipient?.address?.string,
            'Name': isPickup ? shipment.sender?.name : shipment.recipient?.name,
            'Phone': isPickup ? shipment.sender?.phone : shipment.recipient?.phone,
            'Weight': shipment.weight || 0,
            'Volume': shipment.volume || 0,
            'Delivery Type': shipment.custom_delivery_type || shipment.delivery_partner,
            'Partner Delivery': shipment.last_mile_delivery_partner || '',
            'driverNumber': driverNumber,
            'driver_id': driverId
          };
        });
        
        // Update the driversPlans state with the transformed data
        setDriversPlans(transformedData);
      } catch (error) {
        console.error('Error fetching clusters data:', error);
        toast.error(`Failed to fetch clusters data: ${error.message}`);
      }
      
      // Mark request as completed
      pendingRequestRef.current = false;
      setIsLoading(false);
    } else if (data.status === 'error') {
      console.error('Clustering failed:', data.message || 'Unknown error');
      toast.error(`Clustering failed: ${data.message || 'Unknown error'}`);
      pendingRequestRef.current = false;
      setIsLoading(false);
    } else if (data.status === 'progress') {
      // Handle progress updates if needed
      console.log('Clustering in progress:', data.progress || 0);
      // You could update a progress indicator here
    }
  }
  // Handle route optimization response
  else if (data.script === 'makebestroute') {
    if (data.status === 'success') {
      console.log('Route optimization completed successfully:', data);
      toast.success('Route optimization completed successfully!');
      setIsRouteOptimizing(false);
      
      // Navigate to the OptimalRoutesScreen when optimization is complete
      navigate(`/optimal-routes?delivery_date=${deliveryDate}&number_drivers=${numberOfDrivers}&iteration_id=${currentIterationId}`);
    } else if (data.status === 'error') {
      console.error('Route optimization failed:', data.message || 'Unknown error');
      toast.error(`Route optimization failed: ${data.message || 'Unknown error'}`);
      setIsRouteOptimizing(false);
    } else if (data.status === 'progress') {
      console.log('Route optimization in progress:', data.progress || 0);
    }
  }
}

export default function DeliveryDayClusteringScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addListener } = useWebSocket();
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRouteOptimizing, setIsRouteOptimizing] = useState(false);
  const [numberOfDrivers, setNumberOfDrivers] = useState(2);
  const [currentIterationId, setCurrentIterationId] = useState(null);
  const wsListenerRef = useRef(null);
  const pendingRequestRef = useRef(false);
  const [driversPlans, setDriversPlans] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [editingDriver, setEditingDriver] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddingDriver, setIsAddingDriver] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  console.log('deliveryDate', deliveryDate);
  console.log('numberOfDrivers', numberOfDrivers);
  console.log('selectedDrivers', selectedDrivers);

  // Extract the delivery_date from URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const dateParam = queryParams.get('delivery_date');
    if (dateParam) {
      setDeliveryDate(dateParam);
    }
  }, [location.search]);

  // Fetch drivers data
  useEffect(() => {
    const getDrivers = async () => {
      try {
        const driversData = await fetchDrivers();
        setDrivers(driversData);
      } catch (error) {
        console.error('Failed to fetch drivers:', error);
        toast.error('Failed to load drivers information');
      }
    };

    getDrivers();
  }, []);

  // Replace the useCallback with a simple function that calls the external function
  const handleWebSocketMessage = (data) => {
    processWebSocketMessage(
      data,
      pendingRequestRef,
      deliveryDate,
      numberOfDrivers,
      setDriversPlans,
      setIsLoading,
      setIsRouteOptimizing,
      navigate,
      selectedDrivers,
      setCurrentIterationId,
      currentIterationId
    );
  };

  // Setup WebSocket listener
  useEffect(() => {
    // Register the websocket listener
    const removeListener = addListener(handleWebSocketMessage);
    wsListenerRef.current = removeListener;

    // Cleanup function
    return () => {
      if (wsListenerRef.current) {
        wsListenerRef.current();
      }
    };
  }, [addListener, handleWebSocketMessage]);

  const fetchClusters = async () => {
    if (!deliveryDate) return;
    
    if (selectedDrivers.length === 0) {
      toast.error('Please select at least one driver');
      return;
    }
    
    setIsLoading(true);
    try {
      console.log(`Requesting clusters for delivery date: ${deliveryDate} with drivers: ${selectedDrivers.join(',')}`);
      // Mark that we have a pending request
      pendingRequestRef.current = true;
      
      // Make the API call to initiate clustering
      const response = await createDeliveryClusters(deliveryDate, selectedDrivers);
      console.log('Initial API response:', response);
      
      // The actual clusters will come through the WebSocket
      toast.info(`Requested creation of clusters for ${selectedDrivers.length} drivers. Processing...`);
      
      // Note: Don't set isLoading to false here - we'll wait for the WebSocket response
    } catch (error) {
      console.error('Failed to initiate clustering:', error);
      toast.error('Failed to start the clustering process');
      pendingRequestRef.current = false;
      setIsLoading(false);
    }
  };

  const handlePointUpdate = async (updatedShipment) => {
    console.log('updatedShipment', updatedShipment);

    // Get the driver ID from the selected drivers array based on the new driver number
    const newDriverId = selectedDrivers[updatedShipment.newDriverNumber - 1];
    
    if (!newDriverId) {
      console.error('Could not find driver ID for number:', updatedShipment.newDriverNumber);
      toast.error('Failed to update driver assignment: Invalid driver number');
      return;
    }

    try {
      // Call the API to update the driver assignment in the database
      await updateShipmentDriver(
        updatedShipment['Delivery Name'],
        newDriverId,
        updatedShipment.iterationId || currentIterationId
      );
      
      // Update the local state
    const driversPlansCopy = [...driversPlans];
    const index = driversPlansCopy.findIndex(shipment => shipment['Delivery Name'] === updatedShipment['Delivery Name']);
    if (index !== -1) {
      driversPlansCopy[index] = {
        ...updatedShipment,
        driverNumber: updatedShipment.newDriverNumber
      };
      setDriversPlans(driversPlansCopy);
        
        // Show success message
        toast.success('Driver assignment updated successfully');
      }
    } catch (error) {
      console.error('Error updating driver assignment:', error);
      toast.error(`Failed to update driver assignment: ${error.message}`);
    }
  }

  const handleDriverSelection = (driverId) => {
    setSelectedDrivers(prevSelected => {
      if (prevSelected.includes(driverId)) {
        // If already selected, remove from array
        return prevSelected.filter(id => id !== driverId);
      } else {
        // If not selected, add to array
        return [...prevSelected, driverId];
      }
    });
  };

  const handleEditDriver = (driver) => {
    console.log("Edit driver clicked:", driver);
    setEditingDriver(driver);
    setIsAddingDriver(false);
    setIsEditModalOpen(true);
    console.log("Modal should be open:", true);
  };

  const handleAddDriver = () => {
    console.log("Add driver clicked");
    setEditingDriver(null);
    setIsAddingDriver(true);
    setIsEditModalOpen(true);
    console.log("Modal should be open:", true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingDriver(null);
    setIsAddingDriver(false);
  };

// This function updates the drivers list based on whether we're adding or editing a driver.
const handleDriverSaved = (savedDriver) => {
  if (isAddingDriver) {
    setDrivers(prevDrivers => [...prevDrivers, savedDriver]);
  } else {
    setDrivers(prevDrivers =>
      prevDrivers.map(driver =>
        driver.id === savedDriver.id ? savedDriver : driver
      )
    );
  }
};



// Handles route optimization based on the selected delivery date.
  const handleOptimizeRoutes = async () => {
    if (!deliveryDate) {
      toast.error('Please select a delivery date first');
      return;
    }
    
    setIsRouteOptimizing(true);
    try {
      console.log(`Requesting optimal routes for delivery date: ${deliveryDate} with ${numberOfDrivers} drivers`);
      
    const iterationId = `${Date.now()}`;
    setCurrentIterationId(iterationId);

    const requestObj = {
      deliveryDate: deliveryDate,            
      clustersIterationId: currentIterationId, 
      iterationId
    }
    console.log('Request object:', requestObj);

    const response = await requestOptimalRoutes(requestObj);
    
    // Show info toast that the process has started.
      toast.info(`Requested optimal routes for delivery date ${deliveryDate} with ${numberOfDrivers} drivers. Processing...`);
      
    // Loading state will be cleared when the webhook response is received.
    } catch (error) {
      console.error('Failed to initiate route optimization:', error);
      toast.error(`Failed to start route optimization: ${error.message}`);
      setIsRouteOptimizing(false);
    }
};


  const handleDeleteDriver = (driver) => {
    setDriverToDelete(driver);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDeleteDriver = async () => {
    if (!driverToDelete) return;
    
    try {
      await deleteDriver(driverToDelete.id);
      
      // Remove the driver from the list
      setDrivers(prevDrivers => prevDrivers.filter(driver => driver.id !== driverToDelete.id));
      
      // Also remove from selected drivers if selected
      if (selectedDrivers.includes(driverToDelete.id)) {
        setSelectedDrivers(prev => prev.filter(id => id !== driverToDelete.id));
      }
      
      toast.success('Driver deleted successfully');
      setIsConfirmDeleteOpen(false);
      setDriverToDelete(null);
    } catch (error) {
      toast.error(`Failed to delete driver: ${error.message}`);
    }
  };

  const cancelDeleteDriver = () => {
    setIsConfirmDeleteOpen(false);
    setDriverToDelete(null);
  };

  const navigationButtons = (
    <>
      <button
        onClick={() => navigate('/')}
        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Back to Delivery day overview
      </button>
      <button
        onClick={handleOptimizeRoutes}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
      >
        {'Get Optimal Routes'}
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar buttons={navigationButtons} />
      <div className="h-[calc(100vh-4rem)] flex">
        {/* Drivers Sidebar - Always visible */}
        <div className="w-80 bg-white shadow-md overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Drivers</h2>
          </div>
          
          {drivers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {isLoading ? 'Loading drivers...' : 'No drivers found.'}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {drivers.map((driver, index) => (
                <div key={driver.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className="flex items-center h-5 mt-1">
                        <input
                          id={`driver-${driver.id}`}
                          type="checkbox"
                          checked={selectedDrivers.includes(driver.id)}
                          onChange={() => handleDriverSelection(driver.id)}
                          className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor={`driver-${driver.id}`} className="font-medium text-gray-800 block">
                          {index + 1}) {driver.name}
                        </label>
                        <p className="text-gray-600 mt-1">
                          <span className="font-medium">Home Address:</span> {driver.home_address}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditDriver(driver);
                        }}
                        className="p-1 text-gray-400 hover:text-indigo-600 focus:outline-none"
                        title="Edit driver"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteDriver(driver);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 focus:outline-none"
                        title="Delete driver"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Add Driver Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddDriver();
              }}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add New Driver
            </button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
        {/* Display selected delivery date and controls */}
        {deliveryDate && (
          <div className="bg-white shadow-sm p-3 flex justify-between items-center">
            <div className="text-gray-700">
              Clustering for delivery date: {new Date(deliveryDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchClusters}
                disabled={isLoading || isRouteOptimizing}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
              >
                {isLoading ? 'Optimizing...' : 'Optimize Cluster'}
              </button>
            </div>
          </div>
        )}
        
        {/* Loading indicator */}
        {(isLoading || isRouteOptimizing) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <p className="text-gray-600">
                {isRouteOptimizing 
                  ? 'Creating optimal delivery routes...' 
                  : 'Creating optimal delivery clusters...'}
              </p>
            </div>
          </div>
        )}
        
        {/* Full-screen map for delivery day clustering */}
        <div className="h-[calc(100vh-8rem)]">
          <MarkShipmentsMap 
            shipments={driversPlans} 
            numberOfDrivers={numberOfDrivers}
            onPointUpdate={handlePointUpdate}
            selectedDrivers={selectedDrivers}
            drivers={drivers}
            iterationId={currentIterationId}
          />
        </div>
      </div>
      </div>

      {/* Replace the inline modal with the EditDriverModal component */}
      <EditDriverModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        driver={editingDriver}
        onDriverSaved={handleDriverSaved}
      />

      {/* Delete Confirmation Modal */}
      {isConfirmDeleteOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[9999]"
          onClick={cancelDeleteDriver}
        >
          <div 
            className="bg-white rounded-lg overflow-hidden shadow-xl max-w-md w-full" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 bg-red-50 border-b border-red-100">
              <h3 className="text-lg font-medium text-gray-900">
                Confirm Delete Driver
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the driver <span className="font-medium">{driverToDelete?.name}</span>? This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteDriver}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteDriver}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
